// PRODUCTION — standalone form builder controls
import React, { useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import {
  protoSelectChevronCss,
  protoSelectLabelCss,
  protoSelectMenuCss,
  protoSelectOptionCss,
  protoSelectTriggerCss,
} from '../../flowEditorProtoSelectStyles'

export const SoloSelect = ({
  value,
  options,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
}: {
  value: string
  options: BasicSelectOption[]
  onChange: (v: string) => void
  placeholder?: string
  disabled?: boolean
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const selected = options.find((o) => o.value === value)

  useEffect(() => {
    if (!open) {
      return
    }

    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handler)

    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div ref={ref} css={tw`relative flex-1`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpen((p) => !p)
          }
        }}
        css={protoSelectTriggerCss(disabled, open && !disabled, Boolean(selected))}
      >
        <span css={protoSelectLabelCss}>{selected ? selected.label : placeholder}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          stroke={disabled ? 'var(--mui-shades-300)' : 'var(--mui-shades-400)'}
          css={protoSelectChevronCss(open)}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && !disabled && (
        <div css={protoSelectMenuCss}>
          {options.map((opt) => {
            const isActive = opt.value === value

            return (
              <div
                key={String(opt.value)}
                onClick={() => {
                  onChange(String(opt.value))
                  setOpen(false)
                }}
                css={protoSelectOptionCss(isActive)}
              >
                <span>{opt.label}</span>
                {isActive && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--mui-primary-500)"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const stepperBtnCss = (disabled: boolean) => [
  tw`flex h-[38px] w-9 items-center justify-center border-0 bg-transparent text-lg font-normal text-shades-500 transition-colors border-solid`,
  disabled ? tw`cursor-not-allowed` : tw`cursor-pointer hover:bg-shades-50 hover:text-shades-900`,
]

export const SoloNumberInput = ({
  value,
  onChange,
  min = 0,
  disabled = false,
}: {
  value: number
  onChange: (v: number) => void
  min?: number
  disabled?: boolean
}) => {
  const update = (next: number) => {
    if (disabled) {
      return
    }

    if (next >= min) {
      onChange(next)
    }
  }

  return (
    <div
      css={[
        tw`flex h-[38px] flex-1 items-center overflow-hidden rounded-lg border border-solid border-shades-200 focus-within:border-primary-400`,
        disabled ? tw`bg-shades-50 opacity-[0.72]` : undefined,
      ]}
    >
      <input
        type="number"
        value={value}
        min={min}
        disabled={disabled}
        onChange={(e) => update(Number(e.target.value))}
        css={[
          tw`h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm outline-none border-solid`,
          disabled ? tw`cursor-not-allowed text-shades-500` : tw`cursor-text text-shades-900`,
        ]}
      />
      <div css={tw`flex flex-shrink-0 border-l border-shades-200 border-solid`}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => update(value - 1)}
          css={stepperBtnCss(disabled)}
        >
          -
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => update(value + 1)}
          css={[stepperBtnCss(disabled), tw`border-l border-shades-200 border-solid`]}
        >
          +
        </button>
      </div>
    </div>
  )
}
